import { useState } from 'react';
import { Card, Badge, Modal, toast } from '@/components';
import { useApi } from '@/api/hooks';
import { api } from '@/api/client';
import { useAuthStore } from '@/stores/auth';
import { MODULE_DEFINITIONS } from '@mirai/shared';
import styles from './Settings.module.css';

interface TenantConfig {
  company_name: string;
  license_number: string;
  representative: string;
  address: string;
  tel?: string;
  fax?: string;
  business_hours?: string;
  founded: string;
  plan_name: string;
  max_users: number;
  max_properties: number;
  max_storage_gb: number;
  storage_used_gb: number;
  user_count: number;
  modules: string[];
  enabled_modules: string[];
  planPrice?: number;
  monthly_commission_target?: number;
  settings?: {
    contract?: {
      autoContractedOnDealCompleted?: boolean;
    };
  };
}

interface TenantUser {
  id: string;
  name: string;
  email: string;
  role: string;
  is_active: boolean;
}

const allModules = MODULE_DEFINITIONS.map((m: { id: string; name: string; icon: string; desc: string }) => ({
  key: m.id, name: m.name, icon: m.icon, desc: m.desc,
}));

const roleLabel: Record<string, string> = {
  tenant_owner: 'オーナー', tenant_manager: 'マネージャー', tenant_staff: 'スタッフ',
};

export function Settings() {
  const { user: authUser } = useAuthStore();
  const canManageStaff = authUser?.role === 'tenant_owner' || authUser?.role === 'tenant_manager';
  const { data: config, loading: configLoading, refetch: refetchConfig } = useApi<TenantConfig>('/tenant/config');
  const { data: users, loading: usersLoading, refetch: refetchUsers } = useApi<TenantUser[]>('/tenant/users');
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: '', email: '', role: 'tenant_staff' });
  const [inviteResult, setInviteResult] = useState<{ temp_password: string } | null>(null);
  const [showPlanChange, setShowPlanChange] = useState(false);
  const [planChangeForm, setPlanChangeForm] = useState({ requestedPlan: 'Professional', comment: '' });
  const [editingCompany, setEditingCompany] = useState(false);
  const [companyForm, setCompanyForm] = useState({
    company_name: '', address: '', tel: '', fax: '', business_hours: '',
    license_number: '', representative: '', founded: '',
  });

  if (!canManageStaff) {
    return (
      <div className={styles.page}>
        <Card title="テナント設定">
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink3)', fontSize: 13 }}>
            このページはオーナーまたはマネージャーのみアクセス可能です。
          </div>
        </Card>
      </div>
    );
  }

  if (configLoading || usersLoading) return <div className={styles.page}>読み込み中...</div>;

  const enabledSet = new Set(config?.enabled_modules ?? []);
  const maxUsers = config?.max_users ?? 10;
  const userCount = users?.length ?? 0;
  const planPrice = config?.planPrice ?? 0;
  const formattedPrice = planPrice > 0 ? `¥${planPrice.toLocaleString()}/月` : '—';

  const handleInvite = async () => {
    if (!inviteForm.name || !inviteForm.email) {
      toast('名前とメールアドレスは必須です', 'error');
      return;
    }
    try {
      const result = await api<{ temp_password: string }>('/tenant/users/invite', {
        method: 'POST',
        body: JSON.stringify(inviteForm),
      });
      setInviteResult(result);
      toast('スタッフを招待しました', 'success');
      refetchUsers();
    } catch (e: any) {
      toast(e?.message || '招待に失敗しました', 'error');
    }
  };

  const handlePlanChange = async () => {
    try {
      const currentPlan = config?.plan_name ?? 'Standard';
      await api('/tenant/support-tickets', {
        method: 'POST',
        body: JSON.stringify({
          subject: `プラン変更申請: ${currentPlan} → ${planChangeForm.requestedPlan}`,
          body: planChangeForm.comment || `${planChangeForm.requestedPlan}プランへの変更を希望します。`,
          priority: 'high',
        }),
      });
      toast('プラン変更申請を送信しました', 'success');
      setShowPlanChange(false);
      setPlanChangeForm({ requestedPlan: 'Professional', comment: '' });
    } catch { toast('申請に失敗しました', 'error'); }
  };

  const handleCompanyEdit = () => {
    setCompanyForm({
      company_name: config?.company_name ?? '', address: config?.address ?? '',
      tel: config?.tel ?? '', fax: config?.fax ?? '', business_hours: config?.business_hours ?? '',
      license_number: config?.license_number ?? '', representative: config?.representative ?? '',
      founded: config?.founded ?? '',
    });
    setEditingCompany(true);
  };

  const handleCompanySave = async () => {
    try {
      const body: Record<string, string> = {};
      for (const [k, v] of Object.entries(companyForm)) { if (v) body[k] = v; }
      await api('/tenant/config', { method: 'PATCH', body: JSON.stringify(body) });
      toast('会社情報を保存しました', 'success');
      setEditingCompany(false);
      refetchConfig();
    } catch { toast('保存に失敗しました', 'error'); }
  };

  const closeInviteModal = () => {
    setShowInvite(false);
    setInviteForm({ name: '', email: '', role: 'tenant_staff' });
    setInviteResult(null);
  };

  return (
    <div className={styles.page}>
      <Card title="💎 現在のプラン（住縁 Mirai EMS 管理）">
        <div className={styles.planCard}>
          <div className={styles.planInfo}>
            <div className={styles.planName}>{config?.plan_name ?? 'Standard'} プラン</div>
            <div className={styles.planMeta}>
              <span>{formattedPrice}</span>
              <span>|</span>
              <span>ユーザー {maxUsers}名まで</span>
              <span>|</span>
              <span>ポータル 3媒体</span>
            </div>
          </div>
          <button className={styles.planBtn} onClick={() => setShowPlanChange(true)}>プラン変更を申請</button>
        </div>
        <div className={styles.planNote}>
          プランの変更・アドオンの追加は estate.jiayin.jp から管理者が行います。
        </div>
      </Card>

      <Card title="🎯 月間目標設定">
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div className={styles.formGroup} style={{ flex: 1 }}>
            <label className={styles.formLabel}>今月の目標仲介手数料（円）</label>
            <input className={styles.formInput} type="number"
              defaultValue={config?.monthly_commission_target ?? 500000}
              onBlur={async (e) => {
                const val = Number(e.target.value);
                if (val <= 0) return;
                try {
                  await api('/tenant/config', { method: 'PATCH', body: JSON.stringify({ monthly_commission_target: val }) });
                  toast('目標値を保存しました', 'success');
                } catch { toast('保存に失敗しました', 'error'); }
              }}
            />
          </div>
          <div style={{ fontSize: 13, color: 'var(--ink3)' }}>
            現在の目標: <strong style={{ color: 'var(--ink1)', fontFamily: 'var(--mono)' }}>¥{(config?.monthly_commission_target ?? 500000).toLocaleString()}</strong>
          </div>
        </div>
      </Card>

      <Card title={`📦 有効モジュール（${config?.plan_name ?? 'Standard'} プランに基づく）`}>
        <div className={styles.moduleGrid}>
          {allModules.map((m) => {
            const enabled = enabledSet.has(m.key);
            return (
              <div key={m.key} className={styles.moduleCard}>
                <div className={`${styles.moduleIcon} ${enabled ? styles.enabled : styles.locked}`}>
                  {enabled ? '✓' : '🔒'}
                </div>
                <div className={styles.moduleBody}>
                  <div className={styles.moduleName}>{m.name}</div>
                  <div className={styles.moduleDesc}>{m.desc}</div>
                  <div style={{ fontSize: 10, color: enabled ? 'var(--green)' : 'var(--ink3)', marginTop: 2 }}>
                    {enabled ? `${config?.plan_name ?? 'Standard'} 以上 / 有効` : 'Professional 以上 / 未契約'}
                  </div>
                </div>
                <span className={`${styles.moduleBadge} ${enabled ? styles.enabled : styles.locked}`}>
                  {enabled ? '有効' : '未契約'}
                </span>
              </div>
            );
          })}
        </div>
        <div className={styles.lockNotice}>
          🔒 モジュールの有効/無効は 住縁 Mirai EMS 管理コンソールが制御します。プランのアップグレードまたはアドオン契約で有効化できます。
        </div>
      </Card>

      <Card title="🏢 会社情報・テナント設定">
        {editingCompany ? (
          <div className={styles.companyEditForm}>
            {([
              ['社名', 'company_name'], ['所在地', 'address'], ['TEL', 'tel'], ['FAX', 'fax'],
              ['営業時間', 'business_hours'], ['宅建業免許', 'license_number'], ['代表者', 'representative'], ['設立', 'founded'],
            ] as [string, keyof typeof companyForm][]).map(([label, key]) => (
              <div key={key} className={styles.formGroup}>
                <label className={styles.formLabel}>{label}</label>
                <input className={styles.formInput} value={companyForm[key]} onChange={e => setCompanyForm({ ...companyForm, [key]: e.target.value })}
                  placeholder={label === '営業時間' ? '9:00〜18:00（水曜定休）' : label === 'FAX' ? '03-1234-5679' : ''} />
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
              <button className={styles.planBtn} onClick={() => setEditingCompany(false)}>キャンセル</button>
              <button className={styles.saveBtn} onClick={handleCompanySave}>保存する</button>
            </div>
          </div>
        ) : (
          <>
            <div className={styles.infoGrid}>
              {([
                ['社名', config?.company_name], ['所在地', config?.address],
                ['TEL', config?.tel], ['FAX', config?.fax],
                ['営業時間', config?.business_hours], ['宅建業免許', config?.license_number],
                ['代表者', config?.representative], ['設立', config?.founded],
              ] as [string, string | undefined][]).map(([label, value]) => (
                <div key={label} className={styles.infoItem}>
                  <div className={styles.infoLabel}>{label}</div>
                  <div className={styles.infoValue}>{value || '—'}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 12, textAlign: 'right' }}>
              <button className={styles.saveBtn} onClick={handleCompanyEdit}>✏️ 編集</button>
            </div>
          </>
        )}
      </Card>

      <Card title="🔔 通知設定">
        <div className={styles.notifGrid}>
          {([
            ['新規問い合わせ通知', true], ['契約締結通知', true], ['清掃完了通知', false],
            ['180日上限アラート', true], ['ポータル入稿完了通知', false], ['月次レポート送付', true],
          ] as [string, boolean][]).map(([label, enabled]) => (
            <label key={label} className={styles.notifItem}>
              <input type="checkbox" defaultChecked={enabled} className={styles.notifCheck} onChange={() => toast('通知設定を更新しました', 'success')} />
              <span>{label}</span>
            </label>
          ))}
        </div>
        <div style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 8 }}>
          通知はメールおよびアプリ内で送信されます。
        </div>
      </Card>

      <Card title="🔄 契約ワークフロー">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>取引完了時の自動成約化</div>
            <div style={{ fontSize: 12, color: 'var(--ink3)', lineHeight: 1.6 }}>
              契約取引の全必須書類が電子署名完了した時点で、顧客ステータスを自動で「成約」に遷移させます。
              <br />
              <strong>OFF の場合:</strong> スタッフが CRM から手動で「成約」に変更する運用になります。
            </div>
          </div>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', flexShrink: 0 }}>
            <input
              type="checkbox"
              defaultChecked={config?.settings?.contract?.autoContractedOnDealCompleted ?? true}
              onChange={async (e) => {
                try {
                  await api('/tenant/settings', {
                    method: 'PATCH',
                    body: JSON.stringify({ path: 'contract.autoContractedOnDealCompleted', value: e.target.checked }),
                  });
                  toast(e.target.checked ? '自動成約化を有効にしました' : '自動成約化を無効にしました', 'success');
                } catch { toast('保存に失敗しました', 'error'); }
              }}
              style={{ width: 18, height: 18, cursor: 'pointer' }}
            />
            <span style={{ fontSize: 13, fontWeight: 600 }}>有効</span>
          </label>
        </div>
      </Card>

      {/* スタッフ管理は /tenant/staff へ移動 */}

      {/* プラン変更申請モーダル */}
      <Modal open={showPlanChange} onClose={() => setShowPlanChange(false)} title="プラン変更を申請" footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className={styles.planBtn} onClick={() => setShowPlanChange(false)}>キャンセル</button>
          <button className={styles.saveBtn} onClick={handlePlanChange}>申請する</button>
        </div>
      }>
        <div className={styles.inviteForm}>
          <div style={{ background: 'var(--surface2)', borderRadius: 8, padding: '12px 16px', fontSize: 13 }}>
            <div style={{ fontSize: 11, color: 'var(--ink3)', fontWeight: 600, marginBottom: 4 }}>現在のプラン</div>
            <div style={{ fontWeight: 700 }}>{config?.plan_name ?? 'Standard'} プラン — {formattedPrice}</div>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>希望プラン</label>
            <select className={styles.formInput} value={planChangeForm.requestedPlan} onChange={e => setPlanChangeForm({ ...planChangeForm, requestedPlan: e.target.value })}>
              <option value="Trial">Trial（無料）</option>
              <option value="Standard">Standard（¥25,000/月）</option>
              <option value="Professional">Professional（¥48,000/月）</option>
              <option value="Enterprise">Enterprise（要相談）</option>
            </select>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>変更理由・要望</label>
            <textarea className={styles.formInput} value={planChangeForm.comment} onChange={e => setPlanChangeForm({ ...planChangeForm, comment: e.target.value })} rows={4} style={{ resize: 'vertical', fontFamily: 'inherit' }} placeholder="例：ポータル入稿枠の追加と家賃管理機能を利用したいため..." />
          </div>
        </div>
      </Modal>
    </div>
  );
}
